/**
 * useAudioPlayback - Audio playback management hook
 *
 * Manages TTS audio playback state, including:
 * - Audio source URL
 * - Playing state
 * - Audio unlock for browser autoplay policies
 *
 * @refactored January 2026 - Phase 0 light refactoring
 */

import { useState, useRef, useCallback, RefObject } from 'react';
import type ReactAudioPlayer from 'react-audio-player';

import { ensurePlaybackAudioContextRunning } from '../utils/playbackAudioContext';

function writeAscii(view: DataView, offset: number, value: string): void {
  for (let index = 0; index < value.length; index += 1) {
    view.setUint8(offset + index, value.charCodeAt(index));
  }
}

function createSilentWavUrl(durationMs = 80): string {
  const sampleRate = 8000;
  const channelCount = 1;
  const bytesPerSample = 2;
  const frameCount = Math.max(1, Math.round(sampleRate * (durationMs / 1000)));
  const dataSize = frameCount * channelCount * bytesPerSample;
  const buffer = new ArrayBuffer(44 + dataSize);
  const view = new DataView(buffer);

  writeAscii(view, 0, 'RIFF');
  view.setUint32(4, 36 + dataSize, true);
  writeAscii(view, 8, 'WAVE');
  writeAscii(view, 12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, channelCount, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * channelCount * bytesPerSample, true);
  view.setUint16(32, channelCount * bytesPerSample, true);
  view.setUint16(34, bytesPerSample * 8, true);
  writeAscii(view, 36, 'data');
  view.setUint32(40, dataSize, true);

  return URL.createObjectURL(new Blob([buffer], { type: 'audio/wav' }));
}

export interface AudioPlaybackState {
  audioSource: string | null;
  isPlaying: boolean;
  audioPlayerRef: RefObject<ReactAudioPlayer | null>;
}

export interface AudioPlaybackActions {
  setAudioSource: (url: string | null) => void;
  setPlaying: (playing: boolean) => void;
  unlockAudio: () => Promise<void>;
  stopAudio: () => void;
}

export type UseAudioPlaybackReturn = AudioPlaybackState & AudioPlaybackActions;

/**
 * Serialize unlock attempts across ALL callers (page-global, not per-hook):
 * a mic tap runs unlockAudio from both the host's sound-consent path and
 * ensureVoiceBrowserAccess concurrently. When two silent-wav primes
 * interleave on the same element, each captures the other's transient
 * `muted=true` as the "previous" state to restore, and the element comes
 * out permanently muted — the avatar then "speaks" with moving visemes and
 * zero audible output (Keith's silent-speech defect, root-caused via live
 * console probe 6 Aug 2026).
 */
let unlockInFlight: Promise<void> | null = null;

/**
 * useAudioPlayback - Manage TTS audio playback
 *
 * @returns State and actions for audio playback
 */
export function useAudioPlayback(): UseAudioPlaybackReturn {
  const [audioSource, setAudioSource] = useState<string | null>(null);
  const [isPlaying, setPlaying] = useState(false);
  const audioPlayerRef = useRef<ReactAudioPlayer>(null);

  /**
   * Unlock audio playback - must be called from user gesture
   * Plays a silent audio to satisfy browser autoplay policy
   */
  const unlockAudio = useCallback(async () => {
    const tryResumeAudioContext = async () => {
      try {
        await ensurePlaybackAudioContextRunning();
      } catch (contextErr) {
        console.log('AudioContext unlock attempt failed:', contextErr);
      }
    };

    const tryPrimeElement = async (audioEl: HTMLAudioElement | null, label: string) => {
      if (!audioEl) {
        return;
      }

      audioEl.setAttribute('playsinline', '');
      audioEl.setAttribute('autoplay', '');

      const hadPlayableSource = Boolean(audioEl.src)
        || (audioEl.srcObject instanceof MediaStream && audioEl.srcObject.getAudioTracks().length > 0);

      // Element already playing real content: it IS unlocked. The old code
      // muted + paused it anyway, killing live TTS mid-sentence while the
      // viseme clock kept running (mouth moving, no sound). Never touch a
      // playing element.
      if (hadPlayableSource && !audioEl.paused) {
        return;
      }

      // Element holds real content but is paused — the autoplay-blocked /
      // interrupted state. The strongest unlock inside a user gesture is to
      // actually resume it, unmuted: that both satisfies the policy and
      // rescues the turn the browser blocked. Do NOT mute, do NOT pause.
      if (hadPlayableSource) {
        try {
          await audioEl.play();
          console.log(`✅ ${label} resumed real playback via user gesture`);
        } catch (resumeErr) {
          // Still blocked (or transient) — leave it for the
          // audio-access-required path; never fail the unlock chain.
          console.log(`${label} resume attempt rejected:`, resumeErr);
        }
        return;
      }

      // No source at all: classic silent-wav prime.
      const originalSrc = audioEl.getAttribute('src');
      const temporarySilentUrl = createSilentWavUrl();
      const previousMuted = audioEl.muted;
      audioEl.srcObject = null;
      audioEl.src = temporarySilentUrl;
      audioEl.load();

      try {
        audioEl.muted = true;
        await audioEl.play().catch(() => { });
        audioEl.pause();
        audioEl.currentTime = 0;
        console.log(`✅ ${label} unlocked via user gesture`);
      } catch (elementErr) {
        console.log(`${label} unlock attempt (may already be unlocked):`, elementErr);
      } finally {
        audioEl.muted = previousMuted;
        if (originalSrc) {
          audioEl.src = originalSrc;
        } else {
          audioEl.removeAttribute('src');
        }
        audioEl.load();
        URL.revokeObjectURL(temporarySilentUrl);
      }
    };

    // Coalesce concurrent unlock attempts (see unlockInFlight note above).
    if (unlockInFlight) {
      await unlockInFlight;
      return;
    }

    unlockInFlight = (async () => {
      try {
        await tryResumeAudioContext();
        await tryPrimeElement(audioPlayerRef.current?.audioEl?.current ?? null, 'ReactAudioPlayer');
        await tryPrimeElement(document.getElementById('remoteAudio') as HTMLAudioElement | null, 'Remote audio');
      } catch (unlockErr) {
        console.log('Audio unlock attempt (may already be unlocked):', unlockErr);
      }
    })();

    try {
      await unlockInFlight;
    } finally {
      unlockInFlight = null;
    }
  }, []);

  /**
   * Stop audio playback
   */
  const stopAudio = useCallback(() => {
    const audioEl = audioPlayerRef.current?.audioEl?.current;
    if (audioEl) {
      audioEl.pause();
      audioEl.currentTime = 0;
    }
    setAudioSource(null);
    setPlaying(false);
  }, []);

  return {
    audioSource,
    isPlaying,
    audioPlayerRef,
    setAudioSource,
    setPlaying,
    unlockAudio,
    stopAudio,
  };
}

export default useAudioPlayback;
